import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, ChevronDown, ChevronUp, Truck, CheckCircle, Clock, ArrowRight } from 'lucide-react';
import Header from './components/Header21';
import Footer from './components/Footer21';

const Orders21 = () => {
    const navigate = useNavigate();
    const [expandedOrder, setExpandedOrder] = useState(null);
    const [showEmpty, setShowEmpty] = useState(false);

    const orders = [
        {
            id: 'NOIR-2024001',
            date: '15 Mar 2026',
            status: 'delivered',
            total: 48500,
            items: [
                {
                    name: 'Midnight Zari Lehenga',
                    quantity: 1,
                    size: 'M',
                    price: 48500,
                    image: 'https://images.pexels.com/photos/9419032/pexels-photo-9419032.jpeg?auto=compress&cs=tinysrgb&w=800',
                },
            ],
            shippingAddress: '12 Bandra West, Mumbai, Maharashtra 400050',
            trackingNumber: 'NOIR-TRK-78291',
            deliveredDate: '18 Mar 2026',
        },
        {
            id: 'NOIR-2024002',
            date: '10 Mar 2026',
            status: 'in_transit',
            total: 59500,
            items: [
                {
                    name: 'Noir Silk Saree',
                    quantity: 1,
                    size: 'Free Size',
                    price: 32000,
                    image: 'https://images.pexels.com/photos/11707056/pexels-photo-11707056.jpeg?auto=compress&cs=tinysrgb&w=800',
                },
                {
                    name: 'Embroidered Anarkali Suite',
                    quantity: 1,
                    size: 'S',
                    price: 27500,
                    image: 'https://images.pexels.com/photos/6687182/pexels-photo-6687182.jpeg?auto=compress&cs=tinysrgb&w=800',
                },
            ],
            shippingAddress: '45 Defence Colony, New Delhi 110024',
            trackingNumber: 'NOIR-TRK-78405',
            expectedDelivery: '22 Mar 2026',
        },
        {
            id: 'NOIR-2024003',
            date: '5 Mar 2026',
            status: 'processing',
            total: 27500,
            items: [
                {
                    name: 'Embroidered Anarkali Suite',
                    quantity: 1,
                    size: 'M',
                    price: 27500,
                    image: 'https://images.pexels.com/photos/6687182/pexels-photo-6687182.jpeg?auto=compress&cs=tinysrgb&w=800',
                },
            ],
            shippingAddress: '78 Koramangala, Bengaluru, Karnataka 560034',
        },
    ];

    const statusConfig = {
        delivered: {
            icon: CheckCircle,
            label: 'Delivered',
            badgeBg: 'rgba(46, 204, 113, 0.12)',
            badgeColor: '#2ECC71',
            badgeBorder: 'rgba(46, 204, 113, 0.3)',
        },
        in_transit: {
            icon: Truck,
            label: 'In Transit',
            badgeBg: 'rgba(201, 169, 110, 0.12)',
            badgeColor: '#C9A96E',
            badgeBorder: 'rgba(201, 169, 110, 0.3)',
        },
        processing: {
            icon: Clock,
            label: 'Processing',
            badgeBg: 'rgba(138, 138, 122, 0.12)',
            badgeColor: '#8A8A7A',
            badgeBorder: 'rgba(138, 138, 122, 0.3)',
        },
    };

    const toggleExpand = (orderId) => {
        setExpandedOrder(expandedOrder === orderId ? null : orderId);
    };

    const isEmpty = showEmpty || orders.length === 0;

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400;1,500&family=DM+Sans:wght@300;400;500;600;700&display=swap');
            `}</style>

            <div style={{ fontFamily: "'DM Sans', -apple-system, sans-serif" }} className="min-h-screen bg-[#0A0A0A]">
                <Header />

                <main className="pt-24 pb-16 px-4 lg:px-8">
                    <div className="max-w-[1000px] mx-auto">
                        {/* Page Header */}
                        <div className="mb-10">
                            <p className="text-[#C9A96E] text-sm tracking-[0.3em] uppercase mb-3">My Account</p>
                            <h1
                                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                                className="text-3xl lg:text-5xl font-light text-[#C9A96E] tracking-wide"
                            >
                                Your Orders
                            </h1>
                        </div>

                        {isEmpty ? (
                            /* Empty State */
                            <div className="flex flex-col items-center justify-center py-24">
                                <div className="w-24 h-24 border border-[#2A2A2A] rounded-full flex items-center justify-center mb-8">
                                    <Package className="w-10 h-10 text-[#8A8A7A]" />
                                </div>
                                <h2
                                    style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                                    className="text-2xl lg:text-3xl font-light text-[#F5F0EB] mb-3"
                                >
                                    No orders yet
                                </h2>
                                <p className="text-[#8A8A7A] text-sm mb-8 max-w-sm text-center">
                                    Your order history will appear here once you make your first purchase.
                                </p>
                                <button
                                    onClick={() => navigate('/template21/shop')}
                                    className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#C9A96E] text-[#0A0A0A] text-sm font-semibold tracking-wider uppercase hover:bg-[#D4B87A] transition-colors duration-300"
                                >
                                    Start Shopping
                                    <ArrowRight size={16} />
                                </button>
                            </div>
                        ) : (
                            /* Orders List */
                            <div className="space-y-4">
                                {orders.map((order) => {
                                    const status = statusConfig[order.status];
                                    const StatusIcon = status.icon;
                                    const isExpanded = expandedOrder === order.id;

                                    return (
                                        <div
                                            key={order.id}
                                            className="bg-[#141414] border border-[#2A2A2A] rounded-lg overflow-hidden transition-all duration-300 hover:border-[#C9A96E]/30"
                                        >
                                            {/* Order Header - Always Visible */}
                                            <button
                                                onClick={() => toggleExpand(order.id)}
                                                className="w-full p-5 lg:p-6 text-left"
                                            >
                                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                                    {/* Left: Order Info */}
                                                    <div className="flex-1">
                                                        <div className="flex flex-wrap items-center gap-3 mb-2">
                                                            <h3
                                                                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                                                                className="text-lg font-medium text-[#F5F0EB] tracking-wide"
                                                            >
                                                                {order.id}
                                                            </h3>
                                                            <span
                                                                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium"
                                                                style={{
                                                                    backgroundColor: status.badgeBg,
                                                                    color: status.badgeColor,
                                                                    border: `1px solid ${status.badgeBorder}`,
                                                                }}
                                                            >
                                                                <StatusIcon size={12} />
                                                                {status.label}
                                                            </span>
                                                        </div>
                                                        <p className="text-[#8A8A7A] text-sm">
                                                            Ordered on {order.date}
                                                            <span className="mx-2">&middot;</span>
                                                            {order.items.length} {order.items.length === 1 ? 'item' : 'items'}
                                                        </p>
                                                    </div>

                                                    {/* Right: Total + Expand */}
                                                    <div className="flex items-center gap-4">
                                                        <span
                                                            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                                                            className="text-xl font-semibold text-[#C9A96E]"
                                                        >
                                                            {'\u20B9'}{order.total.toLocaleString('en-IN')}
                                                        </span>
                                                        <div className="text-[#8A8A7A]">
                                                            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Items Preview (Always Visible) */}
                                                <div className="flex gap-3 mt-4 overflow-x-auto">
                                                    {order.items.map((item, idx) => (
                                                        <div key={idx} className="flex items-center gap-3 flex-shrink-0">
                                                            <div className="w-12 h-14 flex-shrink-0 overflow-hidden rounded">
                                                                <img
                                                                    src={item.image}
                                                                    alt={item.name}
                                                                    className="w-full h-full object-cover"
                                                                />
                                                            </div>
                                                            <div className="min-w-0">
                                                                <p className="text-[#F5F0EB] text-sm truncate max-w-[180px]">{item.name}</p>
                                                                <p className="text-[#8A8A7A] text-xs">Qty: {item.quantity}</p>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </button>

                                            {/* Expanded Details */}
                                            {isExpanded && (
                                                <div className="px-5 lg:px-6 pb-6 border-t border-[#2A2A2A]">
                                                    <div className="pt-5 space-y-6">
                                                        {/* Items Detail */}
                                                        <div>
                                                            <h4 className="text-[#C9A96E] text-xs font-medium tracking-wider uppercase mb-4">
                                                                Order Items
                                                            </h4>
                                                            <div className="space-y-4">
                                                                {order.items.map((item, idx) => (
                                                                    <div key={idx} className="flex items-center gap-4 bg-[#0A0A0A] rounded-lg p-3">
                                                                        <div className="w-16 h-20 flex-shrink-0 overflow-hidden rounded">
                                                                            <img
                                                                                src={item.image}
                                                                                alt={item.name}
                                                                                className="w-full h-full object-cover"
                                                                            />
                                                                        </div>
                                                                        <div className="flex-1 min-w-0">
                                                                            <p className="text-[#F5F0EB] text-sm font-medium">{item.name}</p>
                                                                            <p className="text-[#8A8A7A] text-xs mt-0.5">
                                                                                Size: {item.size} &middot; Qty: {item.quantity}
                                                                            </p>
                                                                        </div>
                                                                        <span className="text-[#C9A96E] text-sm font-medium whitespace-nowrap">
                                                                            {'\u20B9'}{item.price.toLocaleString('en-IN')}
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>

                                                        {/* Shipping & Tracking Info */}
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                            <div>
                                                                <h4 className="text-[#C9A96E] text-xs font-medium tracking-wider uppercase mb-2">
                                                                    Shipping Address
                                                                </h4>
                                                                <p className="text-[#8A8A7A] text-sm leading-relaxed">
                                                                    {order.shippingAddress}
                                                                </p>
                                                            </div>
                                                            <div>
                                                                <h4 className="text-[#C9A96E] text-xs font-medium tracking-wider uppercase mb-2">
                                                                    Tracking Details
                                                                </h4>
                                                                {order.trackingNumber ? (
                                                                    <div className="text-sm">
                                                                        <p className="text-[#F5F0EB]">{order.trackingNumber}</p>
                                                                        {order.deliveredDate && (
                                                                            <p className="text-[#2ECC71] text-xs mt-1">
                                                                                Delivered on {order.deliveredDate}
                                                                            </p>
                                                                        )}
                                                                        {order.expectedDelivery && (
                                                                            <p className="text-[#C9A96E] text-xs mt-1">
                                                                                Expected by {order.expectedDelivery}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                    <p className="text-[#8A8A7A] text-sm">
                                                                        Tracking will be available once shipped
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* Order Total Summary */}
                                                        <div className="flex items-center justify-between pt-4 border-t border-[#2A2A2A]">
                                                            <div className="flex gap-3">
                                                                {order.trackingNumber && (
                                                                    <button className="px-5 py-2 border border-[#2A2A2A] text-[#8A8A7A] text-xs font-medium tracking-wider uppercase hover:border-[#C9A96E] hover:text-[#C9A96E] transition-all duration-300 rounded">
                                                                        Track Order
                                                                    </button>
                                                                )}
                                                                {order.status === 'delivered' && (
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            navigate('/template21/shop');
                                                                        }}
                                                                        className="px-5 py-2 bg-[#C9A96E] text-[#0A0A0A] text-xs font-semibold tracking-wider uppercase hover:bg-[#D4B87A] transition-colors duration-300 rounded"
                                                                    >
                                                                        Buy Again
                                                                    </button>
                                                                )}
                                                            </div>
                                                            <div className="text-right">
                                                                <p className="text-[#8A8A7A] text-xs mb-0.5">Order Total</p>
                                                                <p
                                                                    style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                                                                    className="text-xl font-semibold text-[#C9A96E]"
                                                                >
                                                                    {'\u20B9'}{order.total.toLocaleString('en-IN')}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </main>

                <Footer />
            </div>
        </>
    );
};

export default Orders21;
